import { recordPath, type RouteScope } from "./routes";

/**
 * What a change Genie ran made or changed (GEN5-5322 part 2): the record its
 * report names, so cerebro's work ledger remembers it ("the network you made",
 * "that section") after the turn's tool calls are gone. Facts only: read from
 * what the operation returned, never guessed from what Genie asked.
 */
export type WorkRecord = {
  /** `WORK_RECORD_KINDS` in the workspace app; admin names its own (`organization`…). */
  kind: string;
  /** Numbers in the workspace app; an admin record's id can be a string (a scraper's). */
  id: number | string;
  /** As the result named it, at most {@link MAX_WORK_RECORD_NAME} characters. */
  name?: string;
  /** Its page in the app, when it has one. */
  route?: string;
};

/** The longest name a record carries; cerebro's ledger keeps the same. */
export const MAX_WORK_RECORD_NAME = 120;

/**
 * The id keys an SDK operation returns, most specific first: a section is
 * named before its plan, the media item before the stakeholder whose
 * sentiment on it changed, a touchpoint before the request on it.
 */
const ID_KEYS = [
  ["sectionId", "planSection"],
  ["folderId", "planFolder"],
  ["contentId", "media"],
  ["touchpointId", "touchpoint"],
  ["taskId", "task"],
  ["newsletterId", "newsletter"],
  ["planId", "plan"],
  ["networkId", "network"],
  ["stakeholderGroupId", "stakeholderGroup"],
  ["userGroupId", "userGroup"],
  ["customFieldId", "customField"],
  ["mediaFilterId", "mediaFilter"],
  ["dashboardId", "dashboard"],
  ["stakeholderId", "stakeholder"],
] as const;

export type WorkRecordKind = (typeof ID_KEYS)[number][1];

/** The workspace app's record kinds, most specific first. */
export const WORK_RECORD_KINDS: readonly WorkRecordKind[] = ID_KEYS.map(
  ([, kind]) => kind
);

const isRecordObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isId = (value: unknown): value is number | string =>
  (typeof value === "number" && Number.isFinite(value)) ||
  (typeof value === "string" && value.trim() !== "");

/** A name as a result gives it: trimmed, cut to fit, none when empty. */
export const workRecordName = (value: unknown): string | null => {
  if (typeof value !== "string") return null;
  const name = value.trim();
  if (name === "") return null;
  return name.length > MAX_WORK_RECORD_NAME
    ? `${name.slice(0, MAX_WORK_RECORD_NAME - 1)}…`
    : name;
};

/**
 * The keys a result lists its changes under: `changed` (an update), `updated`
 * (a plan's section), `added` and `removed` (tagging, a group's members).
 */
const CHANGE_KEYS = ["changed", "updated", "added", "removed"] as const;

const isNoChange = (value: unknown): boolean =>
  value === false || (Array.isArray(value) && value.length === 0);

/**
 * Whether the result says the change changed nothing: every change list it
 * has is empty or `false` (an update to the values it had, a bookmark already
 * set, touchpoints already in the project: `{planId, added: [],
 * alreadyTagged: [3]}`), or the tag was already on (`alreadyTagged: true`).
 * There is then no work to remember.
 */
const changedNothing = (value: Record<string, unknown>): boolean => {
  if (value["alreadyTagged"] === true) return true;
  const lists = CHANGE_KEYS.filter((key) => key in value);
  return lists.length > 0 && lists.every((key) => isNoChange(value[key]));
};

const KNOWN_ID_KEYS: ReadonlySet<string> = new Set(ID_KEYS.map(([key]) => key));

/**
 * Whether the result names a record no kind stands for (a note, a comment, a
 * stakeholder request): its `name` or `title` may then be that one's, so it
 * is not given to the record found.
 */
const namesAnotherRecord = (value: Record<string, unknown>): boolean =>
  Object.entries(value).some(
    ([key, id]) => key.endsWith("Id") && !KNOWN_ID_KEYS.has(key) && isId(id)
  );

const routeOf = (
  kind: WorkRecordKind,
  id: number | string,
  value: Record<string, unknown>,
  scope: RouteScope
): string | null => {
  switch (kind) {
    case "planSection":
      // A section is seen on its plan's page.
      return isId(value["planId"])
        ? recordPath(scope, "plan", value["planId"])
        : null;
    case "planFolder":
    case "customField":
    case "mediaFilter":
      return null;
    default:
      return recordPath(scope, kind, id);
  }
};

/**
 * The record a value names: the most specific `<kind>Id` at its top level,
 * with its `name` (or `title`) and its page in `scope`. Null when it names
 * none, or says it changed nothing.
 */
const recordIn = (
  value: unknown,
  scope: RouteScope | null,
  { withName }: { withName: boolean }
): WorkRecord | null => {
  if (!isRecordObject(value) || changedNothing(value)) return null;
  const found = ID_KEYS.find(([key]) => isId(value[key]));
  if (found === undefined) return null;
  const [key, kind] = found;
  const id = value[key];
  if (!isId(id)) return null;
  const name = withName
    ? workRecordName(value["name"] ?? value["title"])
    : null;
  const route = scope === null ? null : routeOf(kind, id, value, scope);
  return {
    kind,
    id,
    ...(name !== null && !namesAnotherRecord(value) && { name }),
    ...(route !== null && { route }),
  };
};

/**
 * The record a workspace change made or changed, from what its operation
 * returned (`{planId, sectionId, name}` → the section, on its plan's page).
 */
export const recordOf = (
  result: unknown,
  scope: RouteScope | null
): WorkRecord | null => recordIn(result, scope, { withName: true });

/** Whether a value has an id key no kind stands for, set or not (`noteId: null`). */
const mentionsAnotherRecord = (value: unknown): boolean =>
  isRecordObject(value) &&
  Object.keys(value).some(
    (key) => key.endsWith("Id") && !KNOWN_ID_KEYS.has(key)
  );

/**
 * The record a workspace change that succeeded made or changed (`recordOf`),
 * named from its arguments when its result names only the id (a rename
 * returns `{networkId}`): the change succeeded, so its arguments are what was
 * saved. Only when the arguments name that same record, and neither they nor
 * the result name another one: `stakeholders.addNote`'s title is the note's,
 * never the stakeholder's.
 */
export const recordOfChange = (
  result: unknown,
  args: Record<string, unknown>,
  scope: RouteScope | null
): WorkRecord | null => {
  const record = recordOf(result, scope);
  if (
    record === null ||
    record.name !== undefined ||
    mentionsAnotherRecord(result) ||
    mentionsAnotherRecord(args)
  ) {
    return record;
  }
  const named = recordIn(args, scope, { withName: true });
  if (
    named?.name === undefined ||
    named.kind !== record.kind ||
    String(named.id) !== String(record.id)
  ) {
    return record;
  }
  return { ...record, name: named.name };
};

/** Whether an action creates a record: `plans.create`, `plans.createSection`… */
export const isCreateAction = (name: string): boolean =>
  /\.create(?:[A-Z]|$)/.test(name);

/**
 * The record a workspace change named in its arguments, for one that ran out
 * of time (its result never came): what an update or a delete acted on. None
 * for a create, whose id the server never gave back (its arguments name only
 * where it goes), and no name: the arguments carry the one asked for, which
 * may not be what was saved.
 */
export const recordNamedBy = (
  name: string,
  args: Record<string, unknown>,
  scope: RouteScope | null
): WorkRecord | null =>
  isCreateAction(name) ? null : recordIn(args, scope, { withName: false });
